// Dates et textes des périodes non travaillées, partagés par l'onglet agence « Planning »
// (`agency_calendar_controller.js`) et le tiroir « Réglages » du planning (`plan_settings_controller.js`).
// Jumeau JS de `Plans::CalendarDateFormats` (résumés « 2 → 20 août », plage N8 « du 2 au 20 août ») et
// des règles de dates du concern `NonWorkingCalendar` (années 2000-2100, 366 jours au plus).
//
// Module PUR. Dates en chaînes ISO `AAAA-MM-JJ`, arithmétique en minuit UTC : aucune date locale, aucun
// fuseau (pré-mortem 11, RF-PM6). La comparaison de deux dates ISO est une comparaison de chaînes. Aucun
// texte d'interface ici : tous les mots viennent des gabarits passés par le serveur (`messages`).

import { workCalendarFrom } from './planning_work_calendar.js';

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DAY_MS = 86400000;
const pad = (number, width = 2) => String(number).padStart(width, '0');

// Bornes du concern `NonWorkingCalendar` (YEARS, MAX_PERIOD_DAYS).
export const PERIOD_YEARS = Object.freeze({ min: 2000, max: 2100 });
export const MAX_PERIOD_DAYS = 366;
export const MAX_LABEL_LENGTH = 80;

/** 'AAAA-MM-JJ' → [année, mois, jour] (entiers), null si la forme n'est pas ISO. */
export function isoParts(iso) {
  const match = ISO_DATE.exec(iso || '');
  if (!match) return null;
  return match.slice(1).map((part) => parseInt(part, 10));
}

export function isoFromUtc(utc) {
  return `${pad(utc.getUTCFullYear(), 4)}-${pad(utc.getUTCMonth() + 1)}-${pad(utc.getUTCDate())}`;
}

/** Une date ISO qui existe au calendrier (`2027-02-30` n'existe pas), comme `Date.valid_date?`. */
export function isValidIsoDate(iso) {
  const parts = isoParts(iso);
  if (!parts) return false;
  const [year, month, day] = parts;
  // `setUTCFullYear` et non `Date.UTC`, qui lit les années 0 à 99 comme 1900 à 1999.
  const utc = new Date(Date.UTC(2000, 0, 1));
  utc.setUTCFullYear(year, month - 1, day);
  return isoFromUtc(utc) === iso;
}

export function shiftDays(iso, days) {
  const parts = isoParts(iso);
  if (!parts) return null;
  const [year, month, day] = parts;
  return isoFromUtc(new Date(Date.UTC(year, month - 1, day + days)));
}

export function shiftMonths(iso, months) {
  const parts = isoParts(iso);
  if (!parts) return null;
  const [year, month, day] = parts;
  const first = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)
  ).getUTCDate();
  first.setUTCDate(Math.min(day, lastDay));
  return isoFromUtc(first);
}

/** Jours civils de `start` à `end`, bornes incluses (deux dates valides, `start` ≤ `end`). */
export function daysInclusive(start, end) {
  const [fy, fm, fd] = isoParts(start);
  const [ty, tm, td] = isoParts(end);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / DAY_MS) + 1;
}

/** Jour de la semaine d'une date ISO, convention `getUTCDay()` = `Date#wday` (0 = dimanche). */
export function isoWeekday(iso) {
  const [year, month, day] = isoParts(iso);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

// 'AAAA-MM-JJ' → 'JJ/MM/AAAA', par découpage de chaîne (affichage de `Form::DateInputComponent`).
export function isoToDisplay(iso) {
  const match = ISO_DATE.exec(iso || '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
}

/** Remplace les marqueurs `%{clé}` d'un gabarit I18n ; un marqueur sans valeur reste tel quel. */
export const interpolate = (template, values) =>
  String(template ?? '').replace(/%\{(\w+)\}/g, (marker, key) =>
    key in values ? String(values[key]) : marker
  );

// Pluriel I18n par défaut (aucune règle propre au français dans l'app) : `zero` si présent pour 0, `one` pour 1,
// `other` sinon. Un gabarit sans pluriel (« %{count} j ») est une simple chaîne.
export function pluralize(entry, count, values = {}) {
  if (entry && typeof entry === 'object') {
    const key = count === 0 && entry.zero != null ? 'zero' : count === 1 ? 'one' : 'other';
    return interpolate(entry[key] ?? entry.other, { ...values, count });
  }
  return interpolate(entry, { ...values, count });
}

// Jumeau de `Plans::CalendarDateFormats.period_dates` : « ven. 7 mai », « 2 → 20 août »,
// « 26 juil. → 6 août », « 28 déc. 2027 → 3 janv. 2028 » ; l'année seulement si elle n'est pas l'année
// affichée (`year` nul : l'année toujours).
export function periodDates(from, to, year, messages) {
  const formats = messages.period_formats ?? {};
  const weekdays = messages.short_weekdays ?? [];
  const months = messages.short_months ?? [];
  const [fromYear, fromMonth, fromDay] = from;
  const [toYear, toMonth, toDay] = to;
  const dayMonth = ([, month, day]) =>
    interpolate(formats.day_month, { day, month: months[month] ?? '' });
  const withYear = (text, dateYear) =>
    dateYear === year ? text : interpolate(formats.with_year, { date: text, year: dateYear });

  if (fromYear === toYear && fromMonth === toMonth && fromDay === toDay) {
    const weekday = new Date(Date.UTC(fromYear, fromMonth - 1, fromDay)).getUTCDay();
    const short = interpolate(formats.short_date, {
      weekday: weekdays[weekday] ?? '',
      day: fromDay,
      month: months[fromMonth] ?? '',
    });
    return withYear(short, fromYear);
  }
  if (fromYear !== toYear) {
    return interpolate(formats.span, {
      from: interpolate(formats.with_year, { date: dayMonth(from), year: fromYear }),
      to: interpolate(formats.with_year, { date: dayMonth(to), year: toYear }),
    });
  }
  const text =
    fromMonth === toMonth
      ? interpolate(formats.same_month, { from: fromDay, to: toDay, month: months[toMonth] ?? '' })
      : interpolate(formats.span, { from: dayMonth(from), to: dayMonth(to) });
  return withYear(text, fromYear);
}

// Ponctuation seule (les mots viennent des gabarits) : entre les dates d'un férié, et entre ses dates et son
// motif (« sam. 1 mai · déjà un week-end »), comme `Plans::PlanSettingsView`.
export const NOTE_SEPARATOR = ' · ';
// SS20 : au-delà de trois occurrences d'un même férié, « + N autres » (`MAX_LISTED_DATES` côté Ruby).
export const MAX_LISTED_DATES = 3;

/**
 * Les dates d'un férié dans le tiroir : « lun. 29 mars », « sam. 1 mai 2027 · lun. 1 mai 2028 · mar. 1 mai
 * 2029 + 2 autres » (l'année hors de l'année affichée). Jumeau de `Plans::PlanSettingsView#holiday_dates_text`
 * (`CalendarDateFormats.short_date_in`) ; `messages.more` = pluriel « + %{count} autres ».
 *
 * @param {string[]} dates occurrences ISO, dans l'ordre
 * @param {?number} year l'année affichée
 * @param {object} messages `period_formats`, `short_weekdays`, `short_months`, `more`
 */
export function holidayDatesText(dates, year, messages = {}) {
  const parts = dates.map((iso) => isoParts(iso)).filter(Boolean);
  const listed = parts
    .slice(0, MAX_LISTED_DATES)
    .map((date) => periodDates(date, date, year, messages))
    .join(NOTE_SEPARATOR);
  const rest = parts.length - MAX_LISTED_DATES;
  return rest > 0 ? `${listed} ${pluralize(messages.more, rest)}` : listed;
}

/**
 * Le résumé d'une période (A.5, A·e) : libellé (vide → `default_label`), dates et « N j » (jours ouvrés
 * lun-ven moins les fériés chômés, comme `OrganizationSetting#non_working_period_lost_days`), composés avec
 * les gabarits du serveur. Une date manquante ou inversée vide dates et jours. Parité Ruby :
 * `test/integration/organization_settings/planning_calendar_summary_js_parity_test.rb`.
 *
 * @param {{ start: ?string, end: ?string, label: ?string }} period
 * @param {{ messages?: object, calendar?: object, year: ?number }} context
 * @returns {{ label: string, dates: string, days: string }}
 */
export function periodSummary({ start, end, label }, { messages = {}, calendar = {}, year } = {}) {
  const shown = String(label ?? '').trim() ? String(label) : (messages.default_label ?? '');
  const from = isoParts(start);
  const to = isoParts(end);
  if (!from || !to || start > end) return { label: shown, dates: '', days: '' };

  const days = workCalendarFrom(calendar).workedDaysBetween(start, end);
  return {
    label: shown,
    dates: periodDates(from, to, year, messages),
    days: pluralize(messages.days_short, days),
  };
}

// Le sous-ensemble de `strftime` qu'écrivent les gabarits `date_range` (fr et en) : `%-d` `%d` `%e` `%B`
// `%-m` `%m` `%Y` `%%`. Les noms de mois sont ceux de la locale (`date.month_names`, index 0 nul).
function strftime(parts, format, monthNames) {
  const [year, month, day] = parts;
  const tokens = {
    '-d': String(day),
    d: pad(day),
    e: String(day).padStart(2, ' '),
    B: monthNames[month] ?? '',
    '-m': String(month),
    m: pad(month),
    Y: String(year),
    '%': '%',
  };
  return String(format ?? '').replace(/%(-d|-m|[deBmY%])/g, (marker, token) => tokens[token]);
}

/**
 * La plage d'une période dans le message de chevauchement N8 : « le 7 mai », « du 2 au 20 août »,
 * « du 28 juillet au 3 août », « du 28 décembre 2027 au 3 janvier 2028 ». Jumeau de
 * `Plans::CalendarDateFormats.range_text` (`NonWorkingCalendar#non_working_period_dates`) : mêmes quatre
 * gabarits `planning.non_working_calendar.date_range`, choisis par la même règle.
 *
 * @param {string} start ISO
 * @param {string} end ISO
 * @param {{ same_day, same_month, same_year, other_year: { text, from, to } }} forms les gabarits
 * @param {Array<?string>} monthNames `date.month_names` (index 0 nul)
 * @returns {string} '' si une date est illisible
 */
export function dateRangeText(start, end, forms = {}, monthNames = []) {
  const from = isoParts(start);
  const to = isoParts(end);
  if (!from || !to) return '';
  let kind = 'same_month';
  if (start === end) kind = 'same_day';
  else if (from[0] !== to[0]) kind = 'other_year';
  else if (from[1] !== to[1]) kind = 'same_year';
  const form = forms?.[kind] ?? {};
  return interpolate(form.text, {
    from: strftime(from, form.from, monthNames),
    to: strftime(to, form.to, monthNames),
  });
}

/**
 * L'erreur de dates d'une période, mêmes règles et même ordre que
 * `NonWorkingCalendar#non_working_period_date_error` : dates complètes, fin ≥ début, années 2000-2100,
 * 366 jours au plus. Rend la CLÉ du message (`invalid_dates`, `end_before_start`, `out_of_years`,
 * `too_long`) ou null.
 */
export function periodDateErrorKey(start, end) {
  if (!isValidIsoDate(start) || !isValidIsoDate(end)) return 'invalid_dates';
  if (end < start) return 'end_before_start';
  const years = [isoParts(start)[0], isoParts(end)[0]];
  if (years.some((year) => year < PERIOD_YEARS.min || year > PERIOD_YEARS.max)) {
    return 'out_of_years';
  }
  if (daysInclusive(start, end) > MAX_PERIOD_DAYS) return 'too_long';
  return null;
}
